import React, { ReactNode, useState, MouseEventHandler, useImperativeHandle, forwardRef } from "react";
import IconButton from "./IconButton";
import SVG from "src/utils/SVG";
import Button from "./Button";
import cn from "classnames";
import ModalPortal from "src/modalPortal";
import styled from "styled-components";
import { CSSTransition } from "react-transition-group";

interface IModalProps {
  children: ReactNode;
  className?: string;
  close?: MouseEventHandler<HTMLButtonElement>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onClick2?: MouseEventHandler<HTMLButtonElement> | any;
  leftBtnText?: string;
  rightBtnText?: string;
}
export interface RefProps {
  modalOpen: () => void;
  modalClose: () => void;
}

const Modal = (props: IModalProps, ref: React.Ref<RefProps>) => {
  const [visible, setVisible] = useState(false);

  const onModalOpen = () => {
    setVisible(true);
    // 모달창 열 때 바디 스크롤 잠금
    document.body.style.overflow = "hidden";
  };

  const onModalClose = () => {
    // 모달창 닫을 때 바디 스크롤 풀림
    document.body.style.overflow = "unset";
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    modalOpen: () => onModalOpen(),
    modalClose: () => onModalClose(),
  }));

  return (
    <CSSTransition in={visible} classNames="modal" timeout={300} unmountOnExit>
      <ModalPortal>
        <ModalOverlay className={visible ? "open-modal" : ""}>
          <ModalWrapper className="modal-wrap">
            <ModalHeader>
              <IconButton btnName="팝업닫기" onClick={onModalClose}>
                <SVG name="modalClose" width={24} height={24} viewBox="0 0 24 24" />
              </IconButton>
            </ModalHeader>
            <ModalBody className={cn({ "has-btn": props.leftBtnText || props.rightBtnText })}>
              <div>{props.children}</div>
            </ModalBody>
            <ModalFooter>
              {props.leftBtnText && (
                <Button variant="contained" br="square" color="secondary" onClick={onModalClose}>
                  {props.leftBtnText}
                </Button>
              )}
              {props.rightBtnText && (
                <Button
                  variant="contained"
                  br="square"
                  onClick={() => {
                    props.onClick2!(), onModalClose();
                  }}
                >
                  {props.rightBtnText}
                </Button>
              )}
            </ModalFooter>
          </ModalWrapper>
        </ModalOverlay>
      </ModalPortal>
    </CSSTransition>
  );
};

const ModalOverlay = styled.div`
  z-index: 2000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  background-color: rgba(60, 60, 60, 0.5);
  ${({ theme }) => theme.flexBox.flexMixin("column", "center")};
`;

const ModalWrapper = styled.div`
  z-index: 2001;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto;
  max-height: calc(100% - ${({ theme }) => theme.mobile.notchHeight}px);
  display: block;
  background-color: #fff;
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 15px;
  text-align: right;
  button {
    visibility: hidden;
  }
`;

const ModalBody = styled.div`
  overflow: hidden;
  overflow-y: auto;
  height: auto;
  max-height: calc(100% - 54px - ${({ theme }) => theme.mobile.notchHeight}px);
  padding: 10px 15px 38px;
  font-size: 16px;
  text-align: center;
  &.has-btn {
    max-height: calc(100% - 124px - ${({ theme }) => theme.mobile.notchHeight}px);
  }
`;

const ModalFooter = styled.div`
  padding: 0 15px 30px;
  ${({ theme }) => theme.flexBox.flexMixin()};
  > button {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

export default forwardRef(Modal);
